// GuidelineDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Scale, Shield, AlertTriangle, Lightbulb, Plus, Trash2, CheckCircle, Bot } from 'lucide-react';
import { GUIDELINE_CATEGORIES, ENFORCEMENT_LEVELS } from '../constants';

/**
 * Dialog for adding/editing AI agent guidelines
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {Object|null} props.editingGuideline - Guideline being edited (null for new)
 * @param {Array} props.agents - List of available AI agents
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const GuidelineDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  editingGuideline,
  agents = [],
  onSave,
  onCancel
}) => {
  const getEnforcementIcon = (level) => {
    switch (level) {
      case 'strict': return <Shield className="h-4 w-4 mr-1" />;
      case 'warning': return <AlertTriangle className="h-4 w-4 mr-1" />;
      default: return <Lightbulb className="h-4 w-4 mr-1" />;
    }
  };

  const getEnforcementColor = (level) => {
    switch (level) {
      case 'strict': return 'bg-red-500 hover:bg-red-600';
      case 'warning': return 'bg-amber-500 hover:bg-amber-600';
      default: return 'bg-blue-500 hover:bg-blue-600';
    }
  };

  const getBorderColor = (level) => {
    switch (level) {
      case 'strict': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  const handleAddRule = () => {
    onFormChange({
      ...formData,
      rules: [...formData.rules, { rule: '', explanation: '' }]
    });
  };

  const handleRemoveRule = (idx) => {
    const newRules = formData.rules.filter((_, i) => i !== idx);
    onFormChange({
      ...formData,
      rules: newRules.length ? newRules : [{ rule: '', explanation: '' }]
    });
  };

  const handleRuleChange = (idx, field, value) => {
    const newRules = [...formData.rules];
    newRules[idx][field] = value;
    onFormChange({ ...formData, rules: newRules });
  };

  const toggleAgent = (agentId) => {
    const currentAgents = formData.assigned_agents || [];
    const newAgents = currentAgents.includes(agentId)
      ? currentAgents.filter(id => id !== agentId)
      : [...currentAgents, agentId];
    onFormChange({ ...formData, assigned_agents: newAgents });
  };

  const currentEnforcement = ENFORCEMENT_LEVELS.find(e => e.value === formData.enforcement_level) || ENFORCEMENT_LEVELS[2];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            {editingGuideline ? 'Edit Guideline' : 'Add Guideline'}
          </DialogTitle>
          <DialogDescription>
            Define rules and guidelines for AI agents to follow
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
                placeholder="e.g., Customer Communication Standards"
                data-testid="guideline-title"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={formData.category || 'general'} 
                onValueChange={(val) => onFormChange({ ...formData, category: val })}
              >
                <SelectTrigger data-testid="guideline-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GUIDELINE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Enforcement Level *</Label>
            <div className="flex gap-3 mt-2">
              {ENFORCEMENT_LEVELS.map(level => (
                <Button
                  key={level.value}
                  variant={formData.enforcement_level === level.value ? 'default' : 'outline'}
                  size="sm"
                  className={formData.enforcement_level === level.value ? getEnforcementColor(level.value) : ''}
                  onClick={() => onFormChange({ ...formData, enforcement_level: level.value })}
                  data-testid={`guideline-enforcement-${level.value}`}
                >
                  {getEnforcementIcon(level.value)}
                  {level.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {currentEnforcement.description}
            </p>
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
              placeholder="Describe the overall purpose of this guideline..."
              rows={2}
              data-testid="guideline-description"
            />
          </div>

          {/* Rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Rules</Label>
              <Button variant="outline" size="sm" onClick={handleAddRule} data-testid="guideline-add-rule">
                <Plus className="h-4 w-4 mr-1" />
                Add Rule
              </Button>
            </div>
            <div className="space-y-3">
              {formData.rules?.map((rule, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 border rounded-lg space-y-2 ${getBorderColor(formData.enforcement_level)}`}
                  data-testid={`guideline-rule-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-white flex items-center justify-center text-xs font-bold">
                      {idx + 1}
                    </div>
                    <Input
                      value={rule.rule || ''}
                      onChange={(e) => handleRuleChange(idx, 'rule', e.target.value)}
                      placeholder="Rule statement..."
                      className="flex-1 bg-white"
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveRule(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <Input
                    value={rule.explanation || ''}
                    onChange={(e) => handleRuleChange(idx, 'explanation', e.target.value)}
                    placeholder="Explanation (optional)..."
                    className="bg-white text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Assign to Agents */}
          <div>
            <Label className="flex items-center gap-2">
              <Bot className="h-4 w-4" />
              Enforce for AI Agents
            </Label>
            <p className="text-xs text-muted-foreground mb-2">Select which AI agents must follow this guideline</p>
            <div className="flex flex-wrap gap-2">
              {agents.map(agent => (
                <Badge
                  key={agent.id}
                  variant={formData.assigned_agents?.includes(agent.id) ? 'default' : 'outline'}
                  className={`cursor-pointer ${formData.assigned_agents?.includes(agent.id) 
                    ? formData.enforcement_level === 'strict' ? 'bg-red-500' 
                      : formData.enforcement_level === 'warning' ? 'bg-amber-500'
                      : 'bg-blue-500'
                    : ''}`}
                  onClick={() => toggleAgent(agent.id)}
                  data-testid={`guideline-agent-${agent.id}`}
                >
                  {agent.name}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="guideline-cancel">Cancel</Button>
          <Button onClick={onSave} data-testid="guideline-save">
            <CheckCircle className="h-4 w-4 mr-2" />
            {editingGuideline ? 'Update Guideline' : 'Add Guideline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GuidelineDialog;
